import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { MapPin, Loader2, Globe2, Copy, X, ScanSearch, Sparkles } from "lucide-react";

export const ProspectingTools = ({ onMapsDone }) => {
  const [q, setQ] = useState("");
  const [loc, setLoc] = useState("");
  const [live, setLive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState("");
  const [absorbing, setAbsorbing] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { api.mapsStatus().then((s) => setLive(!!s.live)).catch(() => {}); }, []);

  const runMaps = async () => {
    if (!q.trim() || !loc.trim()) { toast.error("Add an industry and a location."); return; }
    setBusy(true);
    try {
      const { job_id } = await api.mapsProspect(q.trim(), loc.trim());
      toast.message(live ? "Pulling live businesses from Google Maps…" : "Pulling a simulated Maps batch…");
      let n = 0;
      const t = setInterval(async () => {
        n++;
        let job;
        try { job = await api.mapsJob(job_id); } catch { return; }
        if (job.status === "done") {
          clearInterval(t); setBusy(false);
          onMapsDone && onMapsDone();
          toast.success(`Found ${job.count} businesses${job.data_source === "google_places" ? " (live)" : " (demo)"}.`);
        } else if (job.status === "failed" || n > 40) {
          clearInterval(t); setBusy(false);
          toast.error("Maps prospecting failed.");
        }
      }, 4000);
    } catch { toast.error("Maps prospecting failed."); setBusy(false); }
  };

  const runAbsorb = async () => {
    if (!url.trim()) { toast.error("Paste a website URL."); return; }
    setAbsorbing(true);
    try {
      const r = await api.absorb(url.trim(), "email");
      setResult(r);
      toast.success("KURT absorbed their site and wrote a 7-step message.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't read that page."); }
    finally { setAbsorbing(false); }
  };

  const copy = () => { navigator.clipboard?.writeText(result?.seven_step?.message || ""); toast.success("Message copied."); };

  return (
    <div className="mt-2 space-y-2 kz-fade-up">
      {/* Google Maps prospecting */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1 flex items-center gap-2 bg-[#121824] border border-white/10 rounded-xl px-3">
          <MapPin size={16} className={live ? "text-emerald-400" : "text-slate-500"} />
          <input data-testid="maps-query-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Industry (e.g. interior designers)"
            className="flex-1 bg-transparent py-2.5 text-sm outline-none" />
        </div>
        <input data-testid="maps-location-input" value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="Location (e.g. Leeds)"
          className="sm:w-44 bg-[#121824] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500/40" />
        <button data-testid="maps-prospect-btn" onClick={runMaps} disabled={busy}
          className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-teal-500/30 text-teal-300 font-semibold text-sm hover:bg-teal-500/10 disabled:opacity-60 transition-all">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <ScanSearch size={16} />}
          {busy ? "Prospecting…" : `Google Maps${live ? "" : " (demo)"}`}
        </button>
      </div>

      {/* Absorb + 7-step */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1 flex items-center gap-2 bg-[#121824] border border-white/10 rounded-xl px-3">
          <Globe2 size={16} className="text-slate-500" />
          <input data-testid="absorb-url-input" value={url} onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !absorbing && runAbsorb()}
            placeholder="Paste a prospect's website — KURT reads it and writes a 7-step message"
            className="flex-1 bg-transparent py-2.5 text-sm outline-none" />
        </div>
        <button data-testid="absorb-btn" onClick={runAbsorb} disabled={absorbing}
          className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-purple-500/30 text-purple-300 font-semibold text-sm hover:bg-purple-500/10 disabled:opacity-60 transition-all">
          {absorbing ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
          {absorbing ? "Absorbing…" : "Absorb & write"}
        </button>
      </div>

      {result && (
        <div data-testid="absorb-result" className="rounded-2xl border border-purple-500/20 bg-purple-500/[0.04] p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-purple-400 font-bold">7-step personalised message</p>
              <p className="kz-head text-sm font-bold text-slate-100 mt-0.5">{result.absorbed?.title || result.absorbed?.url}</p>
            </div>
            <button data-testid="close-absorb-btn" onClick={() => setResult(null)} className="text-slate-500 hover:text-slate-300"><X size={16} /></button>
          </div>
          {result.seven_step?.analysis && (
            <div className="grid sm:grid-cols-2 gap-2 mt-3">
              {Object.entries(result.seven_step.analysis).map(([k, v]) => (
                <div key={k} className="rounded-lg bg-[#0B0F17] border border-white/[0.06] p-2.5">
                  <span className="text-[10px] uppercase tracking-wider text-purple-300 font-bold">{k}</span>
                  <p className="text-xs text-slate-300 mt-0.5">{v}</p>
                </div>
              ))}
            </div>
          )}
          <div className="mt-3 rounded-xl bg-[#0B0F17] border border-white/[0.06] p-4">
            {result.seven_step?.subject && <p className="text-sm font-semibold text-slate-200 mb-1">{result.seven_step.subject}</p>}
            <p className="text-sm text-slate-400 leading-relaxed whitespace-pre-wrap" data-testid="seven-step-message">{result.seven_step?.message}</p>
          </div>
          <button data-testid="copy-absorb-btn" onClick={copy} className="flex items-center gap-1.5 mt-3 text-xs font-semibold text-purple-300 hover:text-purple-200 transition-colors">
            <Copy size={13} /> Copy message
          </button>
        </div>
      )}
    </div>
  );
};
